import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';

import { ReferenceType } from '../../../../../../../../../../../backend/src/types/types';
import { useGetChunkV2 } from '@/api/queries/useGetChunkV2';
import { useGetDoc } from '@/api/queries/useGetDoc';
import { useGetFactDetails } from '@/api/queries/useGetFactDetails';
import { useGetKimThreadFile } from '@/api/queries/useGetKimThreadFile';
import { CopyIconButton } from '@/new/components/copy-icon-button';
import { DocumentViewer } from '@/new/components/doc-viewer/document-viewer';
import { ErrorBoundary } from '@/new/components/error-boundary';
import { Icon } from '@/new/components/icon';
import { PDFViewer } from '@/new/components/pdf-viewer';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverAnchor, PopoverContent } from '@/new/components/shadcn/popover';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { getFileIconClass, getFileTypeLabel } from '@/new/lib/file-type-display';
import { cn } from '@/new/lib/utils';
import { BoundingBoxWithId, TimeRangeWithId } from '@/types';

const OPEN_DELAY_MS = 300;
const CLOSE_DELAY_MS = 200;

const EMPTY_HIGHLIGHTS: BoundingBoxWithId[] = [];

/**
 * Shared frame for reference previews: source document header above an
 * embedded doc viewer scoped to the reference's highlights (text fallback
 * when the reference has nothing to render).
 */
function PreviewFrame({
  isLoading,
  error,
  doc,
  sourceText,
  viewer,
  onViewInDocument,
}: {
  isLoading: boolean;
  error: Error | null;
  doc: { id: string; name: string; extension: string | null } | null;
  /** The reference's text: what the copy button copies, and the body fallback when there is no viewer */
  sourceText: string | null;
  /** Embedded doc viewer for the reference */
  viewer: ReactNode;
  onViewInDocument?: () => void;
}) {
  if (isLoading) {
    return (
      <div className="w-128">
        <div className="flex items-center gap-2 border-b px-2.5 py-2">
          <Skeleton className="size-5 shrink-0" />
          <div className="min-w-0 flex-1">
            <Skeleton className="mb-1 h-3.5 w-3/5" />
            <Skeleton className="h-3 w-1/4" />
          </div>
          <div className="-mr-0.5 flex items-center">
            <Button variant="ghost" disabled className="p-1 px-1.5">
              <Icon className="lucide--copy size-3.5 text-gray-500" />
            </Button>
            <Button variant="ghost" disabled className="p-1 px-1.5">
              <Icon className="lucide--file-input size-3.5 text-gray-500" />
            </Button>
          </div>
        </div>
        <Skeleton className="h-56 w-full rounded-none" />
      </div>
    );
  }

  if (error || !doc) {
    return <p className="p-4 text-xs text-gray-500">Could not load the reference preview.</p>;
  }

  const fileTypeLabel = getFileTypeLabel(doc.extension);

  const body = viewer ? (
    // pdf.js can race when previews swap quickly; contain any crash to the
    // body instead of the route error boundary
    <ErrorBoundary key={doc.id} fallback={null}>
      <div className="relative h-56 w-128 overflow-hidden bg-gray-100">{viewer}</div>
    </ErrorBoundary>
  ) : sourceText ? (
    <div className="max-h-48 w-128 overflow-y-auto px-4 py-3">
      <p className="text-xs leading-relaxed whitespace-pre-wrap text-gray-600">{sourceText}</p>
    </div>
  ) : null;

  return (
    <>
      <div className={cn('flex items-center gap-2 px-2.5 py-2', body && 'border-b')}>
        <Icon className={cn('size-5', getFileIconClass(doc.extension))} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-gray-900">{doc.name}</p>
          {fileTypeLabel && <p className="text-[11px] text-gray-500">{fileTypeLabel}</p>}
        </div>
        <div className="-mr-0.5 flex items-center">
          {sourceText && (
            <Tooltip>
              <TooltipTrigger asChild>
                <CopyIconButton
                  getText={() => sourceText}
                  className="size-3.5 text-gray-500"
                  buttonClassName="hover:bg-gray-100"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p>Copy reference</p>
              </TooltipContent>
            </Tooltip>
          )}
          {onViewInDocument && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" className="p-1 px-1.5 hover:bg-gray-100" onClick={onViewInDocument}>
                  <Icon className="lucide--file-input size-3.5 text-gray-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Open in document</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
      {body}
    </>
  );
}

interface PreviewContentProps {
  onViewInDocument?: () => void;
  /** Fetch the reference's data without rendering: the doc viewer needs a laid-out container */
  prefetchOnly?: boolean;
}

function ChunkPreviewContent({ chunkId, prefetchOnly, onViewInDocument }: PreviewContentProps & { chunkId: string }) {
  const { matterId } = useParams();
  const { data: chunk, isLoading, error } = useGetChunkV2(matterId, chunkId);

  const highlights: BoundingBoxWithId[] = useMemo(
    () => (chunk?.boundingBoxes ?? []).map((box) => ({ ...box, id: chunkId })),
    [chunk, chunkId],
  );
  const timeHighlights: TimeRangeWithId[] = useMemo(
    () => (chunk?.timeRanges ?? []).map((range) => ({ ...range, id: chunkId })),
    [chunk, chunkId],
  );

  if (prefetchOnly) return null;

  return (
    <PreviewFrame
      isLoading={isLoading}
      error={error}
      doc={chunk ? { id: chunk.docId, name: chunk.doc.file.name, extension: chunk.doc.file.extension } : null}
      sourceText={chunk?.content ?? null}
      viewer={
        chunk ? (
          <DocumentViewer
            matterId={matterId}
            docId={chunk.docId}
            fileExtension={chunk.doc.file.extension ?? undefined}
            highlights={highlights}
            timeHighlights={timeHighlights}
            highlightedFactId={chunk.id}
            hideToolbar
            audioSize="sm"
          />
        ) : null
      }
      onViewInDocument={onViewInDocument}
    />
  );
}

function FactPreviewContent({ factId, prefetchOnly, onViewInDocument }: PreviewContentProps & { factId: string }) {
  const { matterId, subsetId } = useParams();
  const { data: fact, isLoading, isPreviousData, error } = useGetFactDetails(matterId, subsetId, factId);

  // The hook keeps the previous fact's data while a new factId loads (drawer
  // behaviour); the preview shows skeletons instead of the wrong document.
  const isStale = isLoading || isPreviousData;

  // Preview the first location that has something to draw or play; facts can span documents.
  const location =
    fact?.locations.find((candidate) => candidate.boundingBoxes?.length || candidate.timeRanges?.length) ??
    fact?.locations[0];

  const highlights: BoundingBoxWithId[] = useMemo(
    () => (location?.boundingBoxes ?? []).map((box) => ({ ...box, id: factId })),
    [location, factId],
  );
  const timeHighlights: TimeRangeWithId[] = useMemo(
    () => (location?.timeRanges ?? []).map((range) => ({ ...range, id: factId })),
    [location, factId],
  );

  if (prefetchOnly) return null;

  return (
    <PreviewFrame
      isLoading={isStale}
      error={error}
      doc={
        location ? { id: location.docId, name: location.doc.file.name, extension: location.doc.file.extension } : null
      }
      sourceText={fact?.description ?? null}
      viewer={
        location ? (
          <DocumentViewer
            matterId={matterId}
            docId={location.docId}
            fileExtension={location.doc.file.extension ?? undefined}
            highlights={highlights}
            timeHighlights={timeHighlights}
            highlightedFactId={factId}
            hideToolbar
            audioSize="sm"
          />
        ) : null
      }
      onViewInDocument={onViewInDocument}
    />
  );
}

function DocumentPreviewContent({ docId, prefetchOnly, onViewInDocument }: PreviewContentProps & { docId: string }) {
  const { matterId } = useParams();
  const { data: doc, isLoading, error } = useGetDoc(matterId, docId);

  if (prefetchOnly) return null;

  return (
    <PreviewFrame
      isLoading={isLoading}
      error={error instanceof Error ? error : null}
      doc={doc ? { id: doc.id, name: doc.file.name, extension: doc.file.extension } : null}
      sourceText={doc?.summary ?? null}
      viewer={
        doc ? (
          <DocumentViewer
            matterId={matterId}
            docId={doc.id}
            fileExtension={doc.file.extension ?? undefined}
            highlights={EMPTY_HIGHLIGHTS}
            hideToolbar
            audioSize="sm"
          />
        ) : null
      }
      onViewInDocument={onViewInDocument}
    />
  );
}

function ThreadFileChunkPreviewContent({
  chunkId,
  prefetchOnly,
  onViewInDocument,
}: PreviewContentProps & { chunkId: string }) {
  const { matterId, subsetId, threadId } = useParams();
  const { data: file, isLoading, isProcessing, error } = useGetKimThreadFile({ matterId, subsetId, threadId, chunkId });

  const fileMetadata = useMemo(
    () => (file ? { name: file.name, extension: file.extension, pageDimensions: file.pageDimensions } : undefined),
    [file],
  );
  const highlights: BoundingBoxWithId[] = useMemo(
    () => (file?.chunk ? [{ ...file.chunk.boundingBox, id: file.chunk.id }] : []),
    [file],
  );

  if (prefetchOnly) return null;

  // Thread files are ad-hoc uploads without page rasters, so the viewer
  // renders their converted PDF directly (text fallback when that's missing)
  return (
    <PreviewFrame
      isLoading={isLoading || isProcessing}
      error={!isProcessing && error ? error : null}
      doc={file ? { id: file.id, name: file.name, extension: file.extension } : null}
      sourceText={file?.chunk?.content ?? null}
      viewer={
        file?.url ? (
          <PDFViewer
            docId={file.id}
            fileUrl={file.url}
            fileMetadata={fileMetadata}
            highlights={highlights}
            highlightedFactId={file.chunk?.id}
            hideToolbar
          />
        ) : null
      }
      onViewInDocument={onViewInDocument}
    />
  );
}

interface PreviewTarget {
  id: string;
  referenceType: ReferenceType;
  anchor: HTMLElement;
}

function PreviewContent({ target, prefetchOnly, onViewInDocument }: PreviewContentProps & { target: PreviewTarget }) {
  switch (target.referenceType) {
    case ReferenceType.Fact:
      return <FactPreviewContent factId={target.id} prefetchOnly={prefetchOnly} onViewInDocument={onViewInDocument} />;
    case ReferenceType.Document:
      return (
        <DocumentPreviewContent docId={target.id} prefetchOnly={prefetchOnly} onViewInDocument={onViewInDocument} />
      );
    case ReferenceType.ThreadFileChunk:
      return (
        <ThreadFileChunkPreviewContent
          chunkId={target.id}
          prefetchOnly={prefetchOnly}
          onViewInDocument={onViewInDocument}
        />
      );
    default:
      return (
        <ChunkPreviewContent chunkId={target.id} prefetchOnly={prefetchOnly} onViewInDocument={onViewInDocument} />
      );
  }
}

interface ReferencePreviewContextValue {
  show: (target: PreviewTarget) => void;
  scheduleHide: () => void;
  hideNow: () => void;
}

const ReferencePreviewContext = createContext<ReferencePreviewContextValue | null>(null);

/**
 * Hosts the single hover preview popover for every reference chip beneath it.
 */
export function ReferencePreviewProvider({
  onViewInDocument,
  children,
}: {
  onViewInDocument?: (id: string, referenceType: ReferenceType) => void;
  children: ReactNode;
}) {
  const [target, setTarget] = useState<PreviewTarget | null>(null);
  const [open, setOpen] = useState(false);
  // Gates the slide-to-anchor transition: only once the popover has settled
  // after opening, otherwise the initial off-screen placement would animate too.
  const [isSlideEnabled, setIsSlideEnabled] = useState(false);
  const isOpenRef = useRef(false);
  const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      clearTimeout(openTimer.current);
      clearTimeout(closeTimer.current);
    },
    [],
  );

  useEffect(() => {
    if (!open) {
      setIsSlideEnabled(false);
      return;
    }
    const timer = setTimeout(() => setIsSlideEnabled(true), 250);
    return () => clearTimeout(timer);
  }, [open]);

  const show = useCallback((next: PreviewTarget) => {
    clearTimeout(closeTimer.current);
    setTarget(next);
    if (isOpenRef.current) return;
    // Keep an already-running open timer so sweeping across chips still opens once
    if (openTimer.current === undefined) {
      openTimer.current = setTimeout(() => {
        openTimer.current = undefined;
        isOpenRef.current = true;
        setOpen(true);
      }, OPEN_DELAY_MS);
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearTimeout(openTimer.current);
    openTimer.current = undefined;
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      isOpenRef.current = false;
      setOpen(false);
    }, CLOSE_DELAY_MS);
  }, []);

  const cancelHide = useCallback(() => clearTimeout(closeTimer.current), []);

  const hideNow = useCallback(() => {
    clearTimeout(openTimer.current);
    openTimer.current = undefined;
    clearTimeout(closeTimer.current);
    isOpenRef.current = false;
    setOpen(false);
  }, []);

  const contextValue = useMemo(() => ({ show, scheduleHide, hideNow }), [show, scheduleHide, hideNow]);

  // A fresh object per target so Radix re-measures when the anchor chip changes
  const virtualAnchor = useMemo(
    () => (target ? { getBoundingClientRect: () => target.anchor.getBoundingClientRect() } : null),
    [target],
  );

  const handleViewInDocument =
    onViewInDocument && target
      ? () => {
          hideNow();
          onViewInDocument(target.id, target.referenceType);
        }
      : undefined;

  return (
    <ReferencePreviewContext.Provider value={contextValue}>
      {children}
      {/* Prefetch the reference's data while the open delay runs; the viewer
          itself only mounts once visible since it needs a laid-out container */}
      {target && !open && <PreviewContent target={target} prefetchOnly />}
      {target && virtualAnchor && (
        <Popover open={open} onOpenChange={(next) => !next && hideNow()}>
          <PopoverAnchor virtualRef={{ current: virtualAnchor }} />
          <PopoverContent
            align="start"
            disableScrollLock
            onOpenAutoFocus={(event) => event.preventDefault()}
            onMouseEnter={cancelHide}
            onMouseLeave={scheduleHide}
            className={cn(
              // w-fit so a header-only preview (no renderable body) shrinks to its content
              'w-fit max-w-128 overflow-hidden p-0 font-sans shadow-lg/10',
              // Slide to the newly hovered chip instead of teleporting
              isSlideEnabled &&
                '[[data-radix-popper-content-wrapper]:has(>&)]:[transition:transform_250ms_cubic-bezier(0.22,1,0.36,1)]',
            )}
          >
            <PreviewContent target={target} onViewInDocument={handleViewInDocument} />
          </PopoverContent>
        </Popover>
      )}
    </ReferencePreviewContext.Provider>
  );
}

/**
 * Hover wrapper for a reference chip: points the shared preview popover at this
 * chip. Renders the bare chip when no provider is mounted (e.g. artifact cards).
 */
export function ReferencePreviewPopover({
  id,
  referenceType,
  children,
}: {
  id: string;
  referenceType: ReferenceType;
  children: ReactNode;
}) {
  const preview = useContext(ReferencePreviewContext);

  if (!preview) return <>{children}</>;

  return (
    <span
      onMouseEnter={(event) => preview.show({ id, referenceType, anchor: event.currentTarget })}
      onMouseLeave={preview.scheduleHide}
      onClickCapture={preview.hideNow}
    >
      {children}
    </span>
  );
}
